import { Box, useTheme } from "@mui/material";
import * as React from "react";
import Chart from "react-apexcharts";
import moment from "moment";
import { DashboardChartCard } from "./DashboardChartCard";
import {
  getXAxisTickAmount,
  renderSalesTooltip,
  useBaseChartOptions,
} from "./chartHelpers";

export const DashboardDailyMetricLineChart = ({
  title,
  data = [],
  index,
  yAxisKey,
  seriesName,
  lineColor,
  integerValues = false,
  valueFormatter = (value) => Number(value || 0).toFixed(2),
}) => {
  const theme = useTheme();
  const baseOptions = useBaseChartOptions();
  const categories = data.map((item) => moment(item.sales_date).format("DD MMM"));
  const pointCount = categories.length;

  const options = {
    ...baseOptions,
    chart: { ...baseOptions.chart, id: `dashboard-${yAxisKey}-line` },
    colors: [lineColor],
    xaxis: {
      ...baseOptions.xaxis,
      categories,
      tickAmount: getXAxisTickAmount(pointCount),
    },
    yaxis: {
      ...baseOptions.yaxis,
      forceNiceScale: true,
      // counts never show fractional ticks
      ...(integerValues && { decimalsInFloat: 0 }),
      labels: {
        ...baseOptions.yaxis.labels,
        formatter: valueFormatter,
      },
    },
    // straight segments: these are daily points with nothing in between to smooth over
    stroke: {
      curve: "straight",
      width: 2,
      lineCap: "round",
    },
    // faint wash under the line so the shape reads at a glance
    fill: {
      type: "solid",
      opacity: 0.08,
    },
    // white ring keeps each point legible where it sits on the line
    markers: {
      size: 4,
      colors: [lineColor],
      strokeColors: theme.palette.background.paper,
      strokeWidth: 2,
      hover: {
        size: 6,
      },
    },
    tooltip: {
      ...baseOptions.tooltip,
      shared: true,
      intersect: false,
      custom: ({ dataPointIndex }) => {
        const item = data[dataPointIndex] || {};
        return renderSalesTooltip(
          moment(item.sales_date).format("DD MMM YYYY"),
          item,
          "net_grand_total_inc",
          "net_grand_total_exc"
        );
      },
    },
  };

  const series = [
    {
      name: seriesName,
      data: data.map((item) => Number(item[yAxisKey] || 0)),
    },
  ];

  return (
    <DashboardChartCard title={title} isEmpty={!data.length}>
      <Box sx={{ px: "8px", pb: "8px" }}>
        <Chart
          key={index}
          options={options}
          series={series}
          type="area"
          width="100%"
          height="361px"
        />
      </Box>
    </DashboardChartCard>
  );
};
